/**
 * doctorclaw/msg 接口类型定义
 * 对应接口：POST /wsui/oe/doctorclaw/msg
 * 新协议：数据结构与 APP 一致，使用 ImFlow 卡片格式
 */

import type { IFkParams } from './common';
import type { IToolTip } from './getMessages';

// ==================== ImFlow 卡片数据结构（cardId: 11412） ====================

export interface ImFlowQuickReplyOption {
    content: string;
    contentId: string;
}

/**
 * ImFlow contentList 中每个 key 对应的内容项
 * contentAdd 是增量文本（SSE 每帧追加），历史消息中为完整内容
 */
export interface ImFlowContentItem {
    component: string; // 'markdown' | 'thinkProcessV2' | 'text' | 'fileCard' | 'mediaList' 等
    contentAdd?: string; // 增量文本内容（markdown/text 类型）
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    contentReplace?: any; // 全量替换对象（thinkProcessV2 类型，每帧完整数据）
    isFinish?: boolean;
    renderType?: number;
    feature?: {
        like?: LikeDislikeFeature;
        dislike?: LikeDislikeFeature;
        copyInfo?: { enable: boolean };
    };
}

/** 点赞/点踩功能配置（来自 SSE feature.like / feature.dislike） */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export interface LikeDislikeFeature {
    actionInfo?: {
        interaction?: string;
        interactionInfo?: {
            url?: string;
            method?: string;
            popupInfo?: {
                type?: string;
                content?: any;
            };
        };
    };
}

export interface ImFlowContentList {
    ids: string[]; // key 的有序列表
    data: Record<string, ImFlowContentItem>;
}

/**
 * ImFlow list 中的每个节点（对应一个气泡/section）
 * sectionId 相同的节点合并为同一气泡
 */
export interface ImFlowItem {
    sectionId: number | string; // 气泡分割键：同 sectionId 追加内容，新 sectionId 新建气泡
    type: string; // 'markdownList' | 'text' 等
    isFinish: boolean;
    contentList?: ImFlowContentList;
    isMockTypeWriter?: boolean;
    renderType?: number;
    quickReplyOptions?: ImFlowQuickReplyOption[];
}

export interface ImFlowData {
    list: ImFlowItem[];
}

// ==================== MsgItemType 结构 ====================

export interface MixMsgMeta {
    ownerType: 1 | 2 | 3; // 1=system, 2=bot, 3=user
    msgId?: string;
    msgKey?: string;
    sessionId?: string;
    sendTime?: string;
    createTime?: string;
    posterInfo?: {
        name?: string;
        avatar?: string;
    };
    rounds?: number;
    needSticky?: boolean;
    showPosterRole?: number;
    ctrl?: Record<string, unknown>;
    passthroughData?: string;
}

export interface MixMsgContent {
    cardId?: number;
    cardName?: string; // 'ImFlow' | 'ImText' | 'ImWelcomeModule' 等
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data?: any; // 不同 cardName 对应不同 data 结构，用 any 保持灵活
}

export interface MixMsgData {
    action: 'append' | 'end' | 'withdraw';
    content?: MixMsgContent;
    feature?: Record<string, unknown>;
    searchReferences?: { isFinish?: boolean };
    ext?: Record<string, unknown>;
}

export interface MixMsgItem {
    type?: string;
    meta: MixMsgMeta;
    data: MixMsgData;
}

// ==================== SSE 响应根结构 ====================

export interface IMixCtrlData {
    firstToken?: boolean;
    convStatus?: string;
    userMsgACK?: {
        userMsgID?: string;
        userMsgKey?: string;
    };
    activityTask?: unknown;
    costInfo?: unknown;
}

// ==================== SSE segment/section 类型（供 useSSEConversation 内部使用） ====================

/**
 * 对话场景类型
 */
export enum ConversationSceneType {
    /** 首页引导 */
    ImWelcomeModuleHome = 'imWelcomeModule_home',
    /** 用户主动输入 */
    UserInput = 'userInput',
    /** 点击追问 */
    QuickReplySelector = 'quickReplySelector',
    /** 图片上传 */
    InputImg = 'inputImg',
}

/**
 * SSE 消息 payload 项（文本消息）
 */
export interface IMsgPayloadItem {
    msgKey: string;
    type?: string;
    sceneType?: string;
    content: string | object;
    contentType: number; // 1=文本, 7=图文混合
}

/**
 * POST /wsui/oe/doctorclaw/msg 请求参数
 */
export interface IConversationParams extends IFkParams {
    chatData: {
        sessionId: string; // 会话 ID（从 coredata 获取）
        sceneType?: string;
    };
    msg: {
        preMsgId?: string;
        payload: IMsgPayloadItem[];
    };
    ctrlData?: {
        /** 新会话首次请求时传 true（对齐 vita mix 页面） */
        firstCall?: boolean;
        tmpBotVersion2?: number;
    };
    businessType?: string;
    appKey?: string;
    v?: string;
    /** 非 firstCall 时传当前 session 下用户提问轮次（含本次），首次=1（对齐 vita mix 页面） */
    multiTurn?: number;
}

/**
 * SSE segment 片段（内部使用，从 ImFlow 转换而来）
 */
export interface ISSESegment {
    segment_idx: string;
    type:
        | 'thinkProcessV2'
        | 'oeReferences'
        | 'markdown'
        | 'followQuestion'
        | 'text'
        | 'appendAsk'
        | 'fileCard'
        | 'mediaList';
    content: string;
    is_finished: boolean;
    tool_tip?: IToolTip;
}

/**
 * SSE section 区块（内部使用，从 ImFlow 转换而来）
 */
export interface ISSESection {
    section_idx: string;
    segments: ISSESegment[];
    tool_tip?: IToolTip;
}

// ==================== SSE 响应类型 ====================

/**
 * doctorclaw/msg SSE 响应
 * data.message[] 是 ImFlow 格式的消息数组
 */
export interface IConversationResponse {
    status: number;
    msg?: string;
    data?: {
        message?: MixMsgItem[];
        ctrlData?: IMixCtrlData;
        toolData?: unknown;
        userData?: unknown;
    };
}
